import { jsx } from "react/jsx-runtime";
import constate from "constate";
import { useRef, useEffect } from "react";
import "../components/Modal/Alert/AlertModal.js";
import CopyAlertModal from "../components/Modal/Alert/CopyAlertModal.js";
import DeleteAlertModal from "../components/Modal/Alert/DeleteAlertModal.js";
import PasteAlertModal from "../components/Modal/Alert/PasteAlertModal.js";
import { useBulkRowSelection } from "./useBulkRowSelection.js";
import { useColumns } from "./useColumnsContext.js";
import { useDataBufferContext } from "./useDataBuffer.js";
import { useModal } from "../hooks/useModal.js";
import { getSelectedCellRange, rowsAreSelected } from "../hooks/useSelectCells.js";
import { GET_ROWS_LIMIT } from "../utils/buffer/TableDataBuffer.js";
import { getEditedValue } from "../utils/cells.js";
import { createClipboardText, writeTextToClipboard, getClipboardData } from "../utils/clipboard.js";
const COPY_PASTE_LIMIT = GET_ROWS_LIMIT;
const useClipboardActions = ({
  tableId,
  tableRef,
  disabled,
  totalRows,
  additionalRows,
  onDownloadTable,
  onActionSuccess
}) => {
  const { selection, getIsBulkSelecting, exitSelectionState } = useBulkRowSelection();
  const { buffer } = useDataBufferContext();
  const isBulkSelecting = useRef(!!getIsBulkSelecting && getIsBulkSelecting());
  const bulkSelection = useRef(selection);
  const { columnConfig: columns, getAbsoluteColumnIndex } = useColumns();
  const selectionCache = useRef({
    selection: [],
    clipboardText: ""
  });
  const rowCounts = useRef({ total: totalRows, additional: additionalRows });
  const copiedTextRef = useRef("");
  const CopyAlert = (close) => /* @__PURE__ */ jsx(CopyAlertModal, { onClose: close, onDownload: onDownloadTable });
  const PasteAlert = (close) => /* @__PURE__ */ jsx(PasteAlertModal, { onClose: close });
  const DeleteAlert = (close) => /* @__PURE__ */ jsx(DeleteAlertModal, { onClose: close });
  const copyAlertModal = useModal({ modal: CopyAlert });
  const pasteAlertModal = useModal({ modal: PasteAlert });
  const deleteAlertModal = useModal({ modal: DeleteAlert });
  useEffect(() => {
    isBulkSelecting.current = !!getIsBulkSelecting && getIsBulkSelecting();
    bulkSelection.current = selection;
  }, [getIsBulkSelecting, selection]);
  useEffect(() => {
    rowCounts.current = { total: totalRows, additional: additionalRows };
  }, [totalRows, additionalRows]);
  const buildCellChange = ({
    position,
    column,
    columnKey,
    value,
    row,
    disabled: disabled2,
    columnDisabled,
    ghost
  }) => {
    return {
      position,
      column,
      columnKey,
      value,
      ghost,
      disabled: disabled2,
      columnDisabled,
      id: row ? row.id : (position + 1).toString(),
      rowIndex: row ? row.rowIndex : position + 1
    };
  };
  const getHasSelection = () => {
    return !!getSelectedCellRange({ tableId });
  };
  const getRow = (rowIndex, nextRows) => {
    return buffer.getRow(rowIndex) ?? (nextRows == null ? void 0 : nextRows.find((r) => r.position === rowIndex));
  };
  const getCell = (columnIndex, row) => {
    const absoluteColumnIndex = getAbsoluteColumnIndex(columnIndex);
    const column = columns[absoluteColumnIndex];
    const bufferColumnIndex = (column == null ? void 0 : column.index) ?? absoluteColumnIndex;
    return {
      bufferColumnIndex,
      column,
      cell: row == null ? void 0 : row.cells[bufferColumnIndex]
    };
  };
  const getIsGhostRow = (row) => {
    return !row && !!rowCounts.current.additional;
  };
  const getBulkSelectedRowCount = () => {
    const selection2 = bulkSelection.current;
    if (!selection2)
      return 0;
    const exceptions = Object.values(selection2.exceptions);
    if (selection2.type === "none") {
      return exceptions.length;
    }
    if (selection2.type === "all") {
      return rowCounts.current.total - exceptions.length;
    }
    return 0;
  };
  const getBulkSelectedRows = () => {
    const selection2 = bulkSelection.current;
    if (!selection2)
      return [];
    if (selection2.type === "none") {
      return Object.values(selection2.exceptions).filter(
        (row) => !!row
      );
    }
    return buffer.bufferedRecords.filter((row) => {
      return !selection2.exceptions[row.position];
    });
  };
  const getCellChange = (columnIndex, rowIndex, row, options, ghost) => {
    const { column, cell, bufferColumnIndex } = getCell(columnIndex, row);
    const columnDisabled = column == null ? void 0 : column.disableClipboardEvents;
    const disabled2 = (cell == null ? void 0 : cell.active) || (cell == null ? void 0 : cell.disabled) || (column == null ? void 0 : column.disabled) || (options && options.ignoreDisableClipboardEvents ? false : columnDisabled);
    if (columnIndex > -1 && !(disabled2 && (options == null ? void 0 : options.filterDisabled)) && !(ghost && (options == null ? void 0 : options.filterGhost))) {
      const value = (options == null ? void 0 : options.value) !== void 0 ? options == null ? void 0 : options.value : cell == null ? void 0 : cell.value;
      return buildCellChange({
        position: rowIndex,
        column: (options == null ? void 0 : options.useAbsoluteColumnIndex) ? bufferColumnIndex : columnIndex,
        columnKey: column.value,
        value: getEditedValue(value, column) ?? value,
        row,
        disabled: disabled2,
        columnDisabled,
        ghost
      });
    }
    return void 0;
  };
  const getBulkSelectedCells = (options) => {
    const selectedCells = [];
    const rows = getBulkSelectedRows();
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
        const change = getCellChange(columnIndex, row.position, row, options);
        if (change)
          selectedCells.push(change);
      }
    }
    return selectedCells;
  };
  const getSelectedCells = (options, nextRows) => {
    const selectedCells = [];
    const selectedCellRange = getSelectedCellRange({ sort: true, tableId });
    if (!selectedCellRange) {
      if (isBulkSelecting.current) {
        return getBulkSelectedCells(options);
      }
      return selectedCells;
    }
    const start = selectedCellRange[0];
    const end = selectedCellRange[1];
    const startRowIndex = start[0] - 1 === -1 ? 0 : start[0] - 1;
    for (let rowIndex = startRowIndex; rowIndex < end[0]; rowIndex++) {
      const row = getRow(rowIndex, nextRows);
      const ghost = getIsGhostRow(row);
      for (let columnIndex = start[1]; columnIndex <= end[1]; columnIndex++) {
        const change = getCellChange(columnIndex, rowIndex, row, options, ghost);
        if (change)
          selectedCells.push(change);
      }
    }
    return selectedCells;
  };
  const getShowPasteLimitAlert = (payload, rowsToPasteCount) => {
    if (!payload.length) {
      return false;
    }
    const hasAllGhostChanges = !payload.find((change) => !change.ghost);
    if (hasAllGhostChanges) {
      return false;
    }
    return rowsToPasteCount > COPY_PASTE_LIMIT;
  };
  const getCopyActionMeta = () => {
    var _a;
    let firstSelectedRow = 0;
    let lastSelectedRow = 0;
    let rowsToCopyCount = 0;
    let columnsToCopyCount = 0;
    let recordsCopied = false;
    const selectedCellRange = getSelectedCellRange({ sort: true, tableId });
    if (selectedCellRange) {
      firstSelectedRow = selectedCellRange[0][0] - 1;
      lastSelectedRow = selectedCellRange[1][0];
      const firstSelectedColumn = selectedCellRange[0][1] - 1;
      const lastSelectedColumm = selectedCellRange[1][1];
      rowsToCopyCount = lastSelectedRow - firstSelectedRow;
      columnsToCopyCount = lastSelectedColumm - firstSelectedColumn;
      recordsCopied = rowsAreSelected(selectedCellRange, columns == null ? void 0 : columns.length);
    } else if (isBulkSelecting.current) {
      const rows = getBulkSelectedRows();
      firstSelectedRow = ((_a = rows[0]) == null ? void 0 : _a.position) ?? 0;
      rowsToCopyCount = getBulkSelectedRowCount();
      recordsCopied = true;
    }
    const copyType = recordsCopied ? "record" : "cell";
    const totalCopyCount = recordsCopied ? rowsToCopyCount : rowsToCopyCount * columnsToCopyCount;
    return {
      firstSelectedRow,
      rowsToCopyCount,
      totalCopyCount,
      copyType
    };
  };
  const getNextPageIfNeeded = async (startingRowPosition, rowCount) => {
    const lastRowPosition = startingRowPosition + rowCount - 1;
    const lastRow = buffer.getRow(lastRowPosition);
    if (!lastRow && lastRowPosition <= rowCounts.current.total) {
      const nextRows = await buffer.loadNextPageIfNeeded();
      return nextRows || [];
    }
    return [];
  };
  const onCopy = async (event) => {
    if (!getHasSelection() && !isBulkSelecting.current) {
      return false;
    }
    const { firstSelectedRow, rowsToCopyCount, totalCopyCount, copyType } = getCopyActionMeta();
    if (rowsToCopyCount > COPY_PASTE_LIMIT) {
      copyAlertModal.open();
      return false;
    }
    const nextRows = await getNextPageIfNeeded(
      firstSelectedRow,
      rowsToCopyCount
    );
    const selection2 = getSelectedCells({}, nextRows);
    const clipboardText = createClipboardText(selection2);
    selectionCache.current = { selection: selection2, clipboardText };
    if (event) {
      copiedTextRef.current = clipboardText;
      writeTextToClipboard(event, clipboardText);
    }
    if (onActionSuccess) {
      onActionSuccess({
        action: "copy",
        count: totalCopyCount,
        type: copyType
      });
    }
    return true;
  };
  const onCut = async (event) => {
    const copyEnabled = await onCopy(event);
    if (disabled || isBulkSelecting.current || !copyEnabled)
      return;
    const payload = getSelectedCells({
      useAbsoluteColumnIndex: true,
      filterDisabled: true,
      filterGhost: true,
      value: null
    });
    if (payload.length) {
      buffer.updateCells(payload);
    }
  };
  const checkClipboardSingleValue = (clipboardData) => {
    return clipboardData.length === 1 && clipboardData[0].length === 1;
  };
  const getPasteArea = () => {
    var _a;
    const selectedCellRange = getSelectedCellRange({ sort: true, tableId });
    if (selectedCellRange == null ? void 0 : selectedCellRange.length) {
      const startingRow = selectedCellRange[0][0] - 1;
      const startingColumn = selectedCellRange[0][1] === -1 ? 0 : selectedCellRange[0][1];
      const rowPasteAreaCount = selectedCellRange[1][0] - startingRow;
      return { startingRow, startingColumn, rowPasteAreaCount };
    } else if (isBulkSelecting.current) {
      const rows = getBulkSelectedRows();
      const startingRow = ((_a = rows[0]) == null ? void 0 : _a.position) ?? 0;
      const rowPasteAreaCount = getBulkSelectedRowCount();
      return { startingRow, startingColumn: 0, rowPasteAreaCount };
    }
    return {};
  };
  const buildPastePayload = (clipboardData, clipboardHasSingleValue, startingRow, startingColumn, rowPasteAreaCount, nextRows) => {
    if (clipboardHasSingleValue) {
      return getSelectedCells({
        useAbsoluteColumnIndex: true,
        filterDisabled: true,
        value: clipboardData[0][0]
      });
    } else {
      const clipboardDataForPasteArea = getClipboardDataForPasteArea(
        clipboardData,
        rowPasteAreaCount
      );
      return buildPayloadFromClipboardData(
        clipboardDataForPasteArea,
        startingRow,
        startingColumn,
        nextRows
      );
    }
  };
  const buildPayloadFromClipboardData = (clipboardData, startingRow, startingColumn, nextRows) => {
    const payload = clipboardData.reduce((memo, row, rowIndex) => {
      const rowFromStartPoint = startingRow + rowIndex;
      const rowData = getRow(rowFromStartPoint, nextRows);
      const ghost = getIsGhostRow(rowData);
      row.forEach((col, columnIndex) => {
        const { cell, column, bufferColumnIndex } = getCell(
          startingColumn + columnIndex,
          rowData
        );
        const disabled2 = (cell == null ? void 0 : cell.active) || (cell == null ? void 0 : cell.disabled) || (column == null ? void 0 : column.disableClipboardEvents) || (column == null ? void 0 : column.disabled);
        if (!disabled2 && column) {
          const value = clipboardData[rowIndex][columnIndex];
          memo.push(
            buildCellChange({
              column: bufferColumnIndex,
              columnKey: column.value,
              value: getEditedValue(value, column) ?? value,
              position: rowFromStartPoint,
              row: rowData,
              ghost
            })
          );
        }
      });
      return memo;
    }, []);
    return payload;
  };
  const getClipboardDataForPasteArea = (clipboardData, rowPasteAreaCount) => {
    if (rowPasteAreaCount > 1 && rowPasteAreaCount > clipboardData.length) {
      if (clipboardData.length === 1) {
        return Array.from({ length: rowPasteAreaCount }, () => clipboardData[0]);
      } else if (rowPasteAreaCount % clipboardData.length === 0) {
        const repeatedClipboardData = [];
        for (let i = 0; i < rowPasteAreaCount; i++) {
          repeatedClipboardData.push(clipboardData[i % clipboardData.length]);
        }
        return repeatedClipboardData;
      }
    }
    return clipboardData;
  };
  const onPaste = async (event) => {
    var _a;
    if (!getHasSelection() && !isBulkSelecting.current) {
      return false;
    }
    let clipboardText;
    if ("clipboardData" in event) {
      clipboardText = ((_a = event.clipboardData) == null ? void 0 : _a.getData("text")) ?? "";
    } else {
      try {
        clipboardText = await navigator.clipboard.readText();
      } catch (err) {
        console.error("Failed to read clipboard:", err);
        return false;
      }
    }
    const clipboardData = getClipboardData(clipboardText);
    const clipboardHasSingleValue = checkClipboardSingleValue(clipboardData);
    const { startingRow, startingColumn, rowPasteAreaCount } = getPasteArea();
    if (startingRow === void 0)
      return;
    const maxPasteArea = Math.max(rowPasteAreaCount, clipboardData.length);
    const nextRows = await getNextPageIfNeeded(startingRow, maxPasteArea);
    const payload = buildPastePayload(
      clipboardData,
      clipboardHasSingleValue,
      startingRow,
      startingColumn,
      rowPasteAreaCount,
      nextRows
    );
    if (getShowPasteLimitAlert(payload, maxPasteArea)) {
      pasteAlertModal.open();
      return false;
    }
    if (payload.length) {
      if (isBulkSelecting.current && exitSelectionState) {
        exitSelectionState(isBulkSelecting.current);
      }
      buffer.updateCells(payload);
    }
    return true;
  };
  return {
    onCopy,
    onCut,
    onPaste,
    copyAlertModal,
    pasteAlertModal,
    deleteAlertModal
  };
};
const [ClipBoardActionsProvider, useClipboardActionsContext] = constate(useClipboardActions);
export {
  ClipBoardActionsProvider,
  useClipboardActionsContext
};
